import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';
import '@/lib/api';

// Authentication Provider & Guards
import { AuthProvider } from '@/context/AuthContext';
import { NotificationProvider } from '@/context/NotificationContext';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import AdminRoute from '@/components/auth/AdminRoute';
import UserRoute from '@/components/auth/UserRoute';

// Public pages
import LandingPage from '@/pages/public/LandingPage';
import AyurvedaShowcase from '@/components/demo/AyurvedaShowcase';

// Unified Authentication Page
import LoginPage from '@/pages/auth/LoginPage';

// Admin pages & layout
import DashboardLayout from '@/layouts/DashboardLayout';
import Dashboard from '@/pages/admin/Dashboard';
import PatientList from '@/pages/admin/PatientList';
import PatientDetail from '@/pages/admin/PatientDetail';
import AddEditPatient from '@/pages/admin/AddEditPatient';
import PrescriptionManagement from '@/pages/admin/PrescriptionManagement';
import AddPrescription from '@/pages/admin/AddPrescription';
import UploadPrescription from '@/pages/admin/UploadPrescription';
import PrescriptionDetail from '@/pages/admin/PrescriptionDetail';
import ReportManagement from '@/pages/admin/ReportManagement';
import ReportDetail from '@/pages/admin/ReportDetail';
import FollowUpManagement from '@/pages/admin/FollowUpManagement';
import MedicineInventory from '@/pages/admin/MedicineInventory';
import UserManagement from '@/pages/admin/users/UserManagement';
import AdminRecoveryConsole from '@/pages/admin/AdminRecoveryConsole';
import AdminSettings from '@/pages/admin/AdminSettings';

// Operational User pages & layout
import UserLayout from '@/layouts/UserLayout';
import UserDashboard from '@/pages/user/UserDashboard';
import UserPrescriptionList from '@/pages/user/UserPrescriptionList';
import UserPrescriptionDetail from '@/pages/user/UserPrescriptionDetail';
import UserUploadPrescription from '@/pages/user/UserUploadPrescription';
import UserHistory from '@/pages/user/UserHistory';

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/showcase" element={<AyurvedaShowcase />} />

              {/* Unified Authentication Route */}
              <Route path="/login" element={<LoginPage />} />

              {/* Protected Admin Routes */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <DashboardLayout />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="patients" element={<PatientList />} />
                <Route path="patients/new" element={<AddEditPatient />} />
                <Route path="patients/edit/:id" element={<AddEditPatient />} />
                <Route path="patients/:id" element={<PatientDetail />} />
                <Route path="prescriptions" element={<PrescriptionManagement />} />
                <Route path="prescriptions/new" element={<AddPrescription />} />
                <Route path="prescriptions/upload" element={<UploadPrescription />} />
                <Route path="prescriptions/:id" element={<PrescriptionDetail />} />
                <Route path="reports" element={<ReportManagement />} />
                <Route path="reports/:id" element={<ReportDetail />} />
                <Route path="followups" element={<FollowUpManagement />} />
                <Route path="inventory" element={<MedicineInventory />} />
                <Route path="recovery" element={<AdminRecoveryConsole />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>

              {/* Protected Operational User Routes */}
              <Route
                path="/user"
                element={
                  <ProtectedRoute>
                    <UserRoute>
                      <UserLayout />
                    </UserRoute>
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/user/dashboard" replace />} />
                <Route path="dashboard" element={<UserDashboard />} />
                <Route path="prescriptions" element={<UserPrescriptionList />} />
                <Route path="prescriptions/upload" element={<UserUploadPrescription />} />
                <Route path="prescriptions/:id" element={<UserPrescriptionDetail />} />
                <Route path="history" element={<UserHistory />} />
              </Route>

              {/* Catch-all fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;